import React, { useCallback, useEffect, useRef } from 'react';
import config from '../config';

// Embeds the separately-deployed LSTM Trading Lab app (own repo:
// Halter-Ferguson-Financial/LSTM, own infra: LSTM-Infra) so it appears to
// live inside tools.hffinancial.com, the same way Trade Tracker feels native
// here, without its static assets being built into this app's own bundle.
function isAllowedProtocol(protocol) {
  return protocol === 'http:' || protocol === 'https:';
}

function normalizeIframeSrc(url) {
  const trimmedUrl = url?.trim();

  if (!trimmedUrl) {
    return '';
  }

  try {
    const normalizedUrl = new URL(trimmedUrl);
    return isAllowedProtocol(normalizedUrl.protocol) ? normalizedUrl.toString() : '';
  } catch (error) {
    try {
      const normalizedUrl = new URL(`https://${trimmedUrl}`);
      return isAllowedProtocol(normalizedUrl.protocol) ? normalizedUrl.toString() : '';
    } catch (fallbackError) {
      return '';
    }
  }
}

function LstmTradingLab() {
  const configuredUrl = config.api.lstmAppUrl?.trim() || '';
  const src = normalizeIframeSrc(configuredUrl);
  const iframeRef = useRef(null);

  const sendAuthToIframe = useCallback(() => {
    if (!src || !iframeRef.current?.contentWindow) return;
    const accessToken = localStorage.getItem('token') || localStorage.getItem('cognito_access_token');
    const refreshToken = localStorage.getItem('cognito_refresh_token') || localStorage.getItem('refresh_token');
    const username = localStorage.getItem('cognito_username') || localStorage.getItem('username');
    iframeRef.current.contentWindow.postMessage(
      {
        type: 'lstm-auth',
        token: accessToken,
        refreshToken,
        username,
      },
      new URL(src).origin,
    );
  }, [src]);

  useEffect(() => {
    if (!src) return undefined;
    const interval = window.setInterval(sendAuthToIframe, 30000);
    window.addEventListener('storage', sendAuthToIframe);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('storage', sendAuthToIframe);
    };
  }, [sendAuthToIframe, src]);

  if (!src) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-warning" role="alert">
          {configuredUrl
            ? 'LSTM Trading Lab URL is invalid.'
            : 'LSTM Trading Lab URL is not configured.'}
          {' '}Set <code>REACT_APP_LSTM_APP_URL</code> to the CloudFront domain
          output by the LSTM-Infra Terraform (see its <code>lstm_frontend_cloudfront_domain</code> output).
        </div>
      </div>
    );
  }

  // Rendered in a full-bleed Layout (App.js), so the frame simply fills the
  // content area; no navbar/padding arithmetic to drift out of date.
  return (
    <div className="lstm-lab-frame" style={{ flex: 1, minHeight: 0, display: 'flex' }}>
      <iframe
        ref={iframeRef}
        title="LSTM Trading Lab"
        src={src}
        sandbox="allow-same-origin allow-scripts allow-forms"
        onLoad={sendAuthToIframe}
        style={{ flex: 1, display: 'block', width: '100%', height: '100%', border: 'none' }}
        allow="clipboard-read; clipboard-write"
      />
    </div>
  );
}

export default LstmTradingLab;
